import { Box, ButtonBase, ClickAwayListener, Typography, makeStyles } from '@material-ui/core';
import React, { useState, useEffect } from 'react';

import { useFormik } from 'formik';
import * as yup from 'yup';
import eventMetricsMap from 'src/hooks/useEventsReporter/event-types';
import useEventsReporter from 'src/hooks/useEventsReporter';
import ValidationTextField from 'src/components/validation-text-field';
import ROUTES from 'src/lib/routes';
import useRouter from 'src/hooks/useRouter';
import { validateZipCode } from 'src/lib/utils/user-utils';
import useStoreData from 'src/hooks/useStoreData';
import { storeCommunity } from 'src/lib/utils/store-utils';
import { Store } from 'src/services/types';
import StoreSearchModal from 'src/components/store-search-modal';
import { getShipmentRates } from 'src/lib/api-client/shipment-services';
import LoadingOverlay from 'src/components/loading-overlay';
import { Body, Heading } from 'src/components/UI';
import COLORS from 'src/lib/colors';
import { mergeClasses } from 'src/stylesheets/utils';
import ShippingErrorInline from '../shipping-errors/shipping-error-inline';

const useStyles = makeStyles((theme) => ({
    mainConatiner: {
        display: 'flex',
        padding: '0 24px 24px 24px',
        gap: '24px',
        flexDirection: 'column',
        '& .MuiFormLabel-root': {
            color: '#444444 !important',
        },
        '& .MuiInputBase-input': {
            padding: '6px 0 0px',
            fontSize: '18px',
        },
    },
    chevron: {
        position: 'absolute',
        left: '68px',
        top: '0px',
    },
    infoContainer: {
        position: 'relative',
        marginBottom: '24px',
    },
    infoTooltipContainer: {
        background: COLORS.textBlack,
        borderRadius: '15px',
        color: COLORS.primaryWhite,
        display: 'flex',
        paddingRight: '25px',
        position: 'absolute',
        top: '-135px',
        maxHeight: '125px',
    },
    closeIcon:{
        background: 'transparent',
        border: '0',
        position: 'absolute',
        right: '8px',
        top: '8px',

    },
    tooltipInfoImage: {
        height: '125px',
    },
    tooltip: {
        '& .MuiTooltip-arrow': {
            overflow: 'unset',
            left: 'auto !important',
            right: '85% !important',
        },
    },
    title: {
        fontSize: '18px',
        fontWeight: 700,
        lineHeight: '22px',
        letterSpacing: '0.200px',
        marginBottom: '20px',
    },
    subHeaderText: {
        fontSize: '16px',
        fontWeight: 400,
        marginTop: 16,
        lineHeight: '22px',
        color: '#1E1E1E',
        maxWidth: 327,
    },
    locationContainer: {
        display: 'flex',
        gap: '5px',
        flexDirection: 'column',
        marginBottom: '6px',
        alignItems: 'flex-start',
    },
    location: {
        display: 'flex',
        gap: '10px',
    },
    locationText: {
        fontSize: '22px',
        fontWeight: 700,
        lineHeight: '25.96px',
        color: '#343434',
        letterSpacing: '-0.75px',
    },
    changeLocation: {
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
        color: '#086DD2',
    },
    getQuoteBtn: {
        background: '#CC0000',
        height: '50px',
        borderRadius: '20px',
        marginTop: '16px',
        '& span': {
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '17px',
            letterSpacing: '0px',
            textAlign: 'center',
            color: '#ffffff',
        },
        '&:disabled': {
            background: '#d3d3d3',
        },
    },
    getQuoteBtnContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    fieldContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    formConatiner: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    },
    inputFieldAlign: {
        fontFamily: 'StaplesNormsRegular',
        width: '100%',
        padding: '10px',
        fontSize: '16px',
        borderRadius: '10px !important',
        border: '1px solid rgb(204, 204, 204)',
        height: '50px',
        outline: 'none',
        boxShadow: 'none !important',
        marginTop: '10px',
        '&:disabled': {
            background: '#f2f2f2 !important',
            border: '0 none',
        },
    },
    required:{
        color: "#343434",
        '&::after': {
            content: '"*"',
            color: 'red',
            marginLeft: '5px',
        },
    },
}));

const getContryCode = (countryCode: string) => {
    switch (countryCode) {
        case 'CA':
            return 1;
        case 'GU':
            return 1;
        case 'MX':
            return 52;
        case 'PR':
            return 1;
        case 'US':
            return 1;
        case 'VI':
            return 1;
        default:
            return 1;
    }
};

const validationSchema = yup.object({
    recipientZipCode: yup
        .string()
        .required('Zip code is a required field.')
        .matches(/^(?:\d{5}|\d{5}-\d{4})$/, 'Zip code is invalid'),
    weight: yup
        .number()
        .required('Weight is a required field.')
        .max(150, 'Weight must be less than or equal to 150.')
        .min(1, 'Weight must be greater than 0.'),
    country: yup.string().required('Country is a required field.'),
});

const ShippingQuoteEstimator = ({
    setShipmentRateInfo,
    initialShipmentValues,
    setInitialShipmentValues,
}: {
    setShipmentRateInfo: (data: any) => void;
    initialShipmentValues: any;
    setInitialShipmentValues: (data: any) => void;
}) => {
    const classes = useStyles();
    const { history } = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const [showTooltip, setShowtooltip] = useState(false);
    const [showWeightTooltip, setShowWeightTooltip] = useState(false);
    const {
        mainStore,
        additionalStores,
        isFetching,
        updateMainStore,
        storeFindErrorMessage,
    } = useStoreData();
    const [error, setError] = useState(false);
    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const { analyticsCustomEvent } = useEventsReporter();

    const [activeField, setActiveField] = useState('');

    const handleNavigation = () => {
        history.push({
            pathname: ROUTES.getQuoteShippingInformation,
            search: '?step=shippingRateDelivery',
        });
    };

    const {
        values,
        handleSubmit,
        handleChange,
        errors,
        touched,
        setFieldError,
    } = useFormik({
        initialValues: {
            recipientZipCode: initialShipmentValues.recipientZipCode,
            weight: initialShipmentValues.weight,
            country: initialShipmentValues.country,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (currentValues) => {
            setError(false);
            setIsLoading(true);
            const bodyData = {
                recipientZipCode: currentValues.recipientZipCode,
                country: currentValues.country,
                recipientcountry: getContryCode(currentValues.country),
                weight: currentValues.weight,
            };
            getShipmentRates(
                mainStore?.address?.postal_code as unknown as string,
                currentValues.recipientZipCode,
                getContryCode(currentValues.country),
                currentValues.country,
                currentValues.weight
            ).then((responseData: any) => {
                if (
                    responseData?.data?.succeeded === 'true' &&
                    responseData?.data?.data?.length
                ) {
                    setShipmentRateInfo(responseData.data);
                    handleNavigation();
                    setInitialShipmentValues(bodyData);
                } else {
                    setError(true);
                }
                setIsLoading(false);
                console.log({ responseData });
            });
            analyticsCustomEvent(eventMetricsMap.shipping, {
                click_text: 'get quote',
                element_location: 'quote estimator',
            });
        },
    });
    useEffect(() => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 2000);
    }, [mainStore]);

    const onStoreSelected = (store: Store) => {
        updateMainStore(store);
        setSearchModalOpen(false);
    };
    const changeLabel = (e: any) => {
        const fieldName = e.type === 'focus' ? e?.target?.name : '';
        setFieldError(fieldName, '');
        setActiveField(fieldName);
    };
    const handleKeyDown = (event: any) => {
        if (event.key === '.') {
            event.preventDefault();
        }
    };
    const handleZipChange = (e: any) => {
        const zipStatus = validateZipCode(e);
        if (zipStatus) {
            if (e.target.value.length === 7 && !e.target.value.includes('-')) {
                // eslint-disable-next-line
                e.target.value = `${e.target.value.slice(
                    0,
                    5
                )}-${e.target.value.slice(5)}`;
                handleChange({
                    target: { value: e.target.value, name: e.target.name },
                });
            } else {
                handleChange({
                    target: { value: e.target.value, name: e.target.name },
                });
            }
        } else {
            e.preventDefault();
        }
    };
    const handleChangeLocation = () => setSearchModalOpen(true);
    const toggleTooltip = () => {
        setShowtooltip(!showTooltip)
    }

    return (
        <>
            {!error ? (
                <div className={classes.mainConatiner} id="get-quote-conatiner">
                    <Typography
                        className={classes.subHeaderText}
                        id="subHeaderText"
                    >
                        Enter your details below and get a quote based on your
                        nearest Staples<sup>&reg;</sup> store.
                    </Typography>
                    <div
                        className={classes.locationContainer}
                        id="locationContainer"
                    >
                        <Typography className={classes.title}>
                            Your store
                        </Typography>
                        <div className={classes.location} id="location">
                            <img
                                src={require('../../../assets/icons/locationRed.svg')}
                                alt="location"
                            />
                            <Typography className={classes.locationText}>
                                {storeCommunity(mainStore) || 'Unknown'}
                            </Typography>
                        </div>
                        <ButtonBase
                            className={classes.changeLocation}
                            onClick={handleChangeLocation}
                        >
                            Change location
                        </ButtonBase>
                    </div>
                    <form
                        onSubmit={handleSubmit}
                        className={classes.formConatiner}
                        id="formConatiner"
                    >
                        <div className={classes.fieldContainer}>
                            <Typography className={classes.title}>
                                Destination
                            </Typography>
                            <div className={classes.infoContainer}>
                            <label
                                    htmlFor="country"  
                                    className={classes.required}
                                >
                                   Country
                                </label>
                                <input
                                    type="text"
                                    id="sender-country-input"
                                    name="country"
                                    value="United States"
                                    disabled
                                    className={classes.inputFieldAlign}
                                />
                            
                                <div className={classes.chevron}>
                                    <button type='button' aria-label='more info' id="quote-tooltip-toggle" onClick={toggleTooltip} style={{ padding: '0px', border: '0', background: 'transparent' }}>
                                        <img
                                            aria-hidden="true"
                                            src={require('../../../assets/icons/iconQuestionSmallBlue.svg')}
                                            alt="more info"
                                        />
                                    </button>
                                   
                                </div>
                                {showTooltip &&
                                  <ClickAwayListener onClickAway={(event) => {
                                   
                                    if ((event.target as HTMLElement).id !== 'quote-tooltip-toggle' && 
                                        !(event.target as HTMLElement).closest('#quote-tooltip-toggle')) {
                                        event.stopPropagation();
                                        setShowtooltip(false);
                                    }
                                      
                                  }}>
                                    <Box className={classes.infoTooltipContainer}>
                                        <button id="quote-tooltip-close" aria-label='close' onClick={toggleTooltip} type='button' className={classes.closeIcon}>
                                            <img src={require('../../../assets/icons/close-icon-white.svg')} alt="close" aria-hidden="true" />
                                        </button>
                                        <Box style={{ padding: '15px' }}>
                                            <Heading type='h3' size={20} lineHeight={24}>Shipping internationally ?</Heading>
                                            <Body weight='light' size={14} padding='5px 0 0 0' >
                                                Visit us at your local Staples store to get started.
                                            </Body>
                                        </Box>
                                        <Box>
                                            <img className={classes.tooltipInfoImage} src={require('../../../assets/InfoTooltipImages/shippingInfoImg.png')} alt="shipping internationally" />
                                        </Box>
                                    </Box>
                                    </ClickAwayListener>
                                }
                            </div>
                            <label htmlFor="recipientZipCode" className={classes.required}>
                                Zip code
                            </label>
                            <input
                                className={classes.inputFieldAlign}
                                id="sender-zipCode-input"
                                name='recipientZipCode'
                                maxLength={10}
                                value={values.recipientZipCode}
                                onChange={handleZipChange}
                                onBlur={(e: any) => changeLabel(e)}
                                onFocus={(e: any) => changeLabel(e)}
                            />
                            { (touched.recipientZipCode &&
                                    Boolean(errors.recipientZipCode) )&& 
                                    
                                    <Body size={12} color ="textRed"
                                    lineHeight={16} margin='10px 0 0 0'
                                    weight="light">{errors.recipientZipCode}</Body>
                                    }
                        
                        </div>
                        <div className={mergeClasses(classes.fieldContainer, classes.infoContainer)}>
                            <Heading type='h4' size={18} weight='bold'>
                                Estimated shipment weight
                            </Heading>
                            <label className={classes.required} htmlFor="estimated-weight-input">
                                Weight (lb.)
                            </label>
                            <input id="estimated-weight-input"
                                name="weight" 
                                type="number"
                                className={classes.inputFieldAlign}
                                onChange={handleChange}
                                value={values.weight}
                                onBlur={(e: any) => changeLabel(e)}
                                onFocus={(e: any) => changeLabel(e)}
                                onKeyDown={handleKeyDown} />
                                { (touched.weight && Boolean(errors.weight)) && 
                                    <Body  size={12} color ="textRed"
                                    lineHeight={16} margin='10px 0 0 0'
                                    weight="light">
                                        {errors.weight}
                                    </Body>
                                }
                                <div className={classes.chevron} style={{ top: '28px', left: '85px' }}>
                                    <button type='button' aria-label='more info' id="quote-weight-tooltip-toggle" onClick={() => setShowWeightTooltip(!showWeightTooltip)} style={{ padding: '0px', border: '0', background: 'transparent' }}>
                                        <img
                                            aria-hidden="true"
                                            src={require('../../../assets/icons/iconQuestionSmallBlue.svg')}
                                            alt="more info"
                                        />
                                    </button>
                                   
                                </div>
                                {showWeightTooltip &&
                                  <ClickAwayListener onClickAway={(event) => {
                                   
                                    if ((event.target as HTMLElement).id !== 'quote-weight-tooltip-toggle' && 
                                        !(event.target as HTMLElement).closest('#quote-weight-tooltip-toggle')) {
                                        event.stopPropagation();
                                        setShowWeightTooltip(false);
                                    }
                                      
                                  }}>
                                    <Box className={classes.infoTooltipContainer} style={{ top: '-100px' }}>
                                        <button id="quote-tooltip-close" aria-label='close'  onClick={() => setShowWeightTooltip(!showWeightTooltip)} type='button' className={classes.closeIcon}>
                                            <img src={require('../../../assets/icons/close-icon-white.svg')} alt="close" aria-hidden="true" />
                                        </button>
                                        <Box style={{ padding: '15px', display: 'flex', alignItems: 'center' }}>
                                           
                                            <Body weight='light' size={14} padding='5px 0 0 0' >
                                            Weight is for rate estimates. Packages are weighed 
                                            at the store before they ship.
                                            </Body>
                                        </Box>
                                        <Box>
                                            <img className={classes.tooltipInfoImage} src={require('../../../assets/InfoTooltipImages/shippingInfoImg.png')} alt="shipping internationally" />
                                        </Box>
                                    </Box>
                                    </ClickAwayListener>
                                }
                            
                        </div>
{!values.recipientZipCode}
                        <div className={classes.getQuoteBtnContainer}>
                            <ButtonBase
                            
                                className={classes.getQuoteBtn}
                                type="submit"
                                disabled= {!values.recipientZipCode || !values.weight}
                            >
                                <span>Get quote</span>
                            </ButtonBase>
                        </div>

                        <Body
                            color="textMediumGray"
                            size={12}
                            weight="light"
                            textAlign="textCenter"
                        >
                            All pricing shown is an estimate; variances in price
                            at time of purchase may occur. Actual price will be
                            calculated in store when package is processed and
                            will be based on: weight, package dimensions, and
                            total travel distance.
                        </Body>
                    </form>
                    <LoadingOverlay
                        variant="wordmark"
                        theme="light"
                        open={isLoading}
                    />
                    {searchModalOpen ? (
                        <StoreSearchModal
                            mainStore={mainStore}
                            nearbyStores={additionalStores}
                            onStoreSelected={onStoreSelected}
                            open={searchModalOpen}
                            ignoreIsActiveScreen
                            handleClose={() => setSearchModalOpen(false)}
                        />
                    ) : null}
                </div>
            ) : (
                <ShippingErrorInline setError={setError} />
            )}
        </>
    );
};

export default ShippingQuoteEstimator;
